


















































import { Component, Prop, Vue } from "vue-property-decorator";
import MenuIconWithMultiSelectionListComponent from "@/components/common/MultiSelectionFilterMenu.vue";
import {
  CHANGE_FILTER_GROUPS,
  ChangeFilterGroup,
  ClientAdministrationChange,
} from "@/data/models/ClientAdministrationRow";
import { getClientAdministrationChanges } from "@/data/repository/ClientAdministrationRepository";
import { SnackbarModule } from "@/store/modules/SnackbarModule";
import { ChangeEntryView, excludedChangeGroups, toChangeEntryView } from "@/utils/ClientAdministrationChanges";

/** The Verlauf under one filter: its pages so far and where the next one starts. */
interface ChangesView {
  /** The groups this list leaves out, sent with every page of it. */
  excluded: ChangeFilterGroup[];
  changes: ClientAdministrationChange[];
  nextCursor: string | null;
  failed: boolean;
}

/**
 * A client's Verlauf on its detail: every change the IRES Admin Client made to it (Stammdaten,
 * Verträge, Kein Echtbetrieb, the Beschreibung target), newest first, 20 at a time. The openings as the
 * client ("Als Mandant angemeldet") only when ticked in the card's filter (#2610071); IRES Cloud leaves
 * the others out, so a page stays full.
 *
 * Changing the filter never shows the loading animation again (Taimoor, 2026-10-19): each filter keeps
 * its own list, the other one is fetched in the background after the first, and until a list arrives
 * the one on screen stays.
 */
@Component({
  components: { "multi-selection-menu-icon": MenuIconWithMultiSelectionListComponent },
})
export default class ClientAdministrationChangesComponent extends Vue {
  @Prop({ required: true }) clientId!: string;

  readonly filterGroups = CHANGE_FILTER_GROUPS;

  /** Each filter's list, by {@link viewKey}; filled with `Vue.set`. */
  views: Record<string, ChangesView> = {};
  /** The list on screen: the current filter's once it is there, else the one shown before. */
  shownKey: string | null = null;
  /** The lists being fetched, so a quick double toggle does not fetch twice. */
  loadingKeys: string[] = [];
  isLoadingMore = false;
  /** The filter groups ticked; none by default, so the Verlauf shows changes only. */
  shownGroups: ChangeFilterGroup[] = [];

  get currentKey(): string {
    return this.viewKey(this.shownGroups);
  }

  get shownView(): ChangesView | null {
    return this.shownKey !== null ? this.views[this.shownKey] ?? null : null;
  }

  /** Only before the very first list; later the previous list stays while the next one loads. */
  get isLoading(): boolean {
    return this.shownView === null;
  }

  get loadFailed(): boolean {
    return this.shownView?.failed === true;
  }

  get nextCursor(): string | null {
    return this.shownView?.nextCursor ?? null;
  }

  /** Whether the list on screen is the current filter's (else "Weitere anzeigen" waits for it). */
  get isCurrentShown(): boolean {
    return this.shownKey === this.currentKey;
  }

  get entries(): ChangeEntryView[] {
    return (this.shownView?.changes ?? []).map((change) =>
      toChangeEntryView(change, (key, values) => this.$t(key, values).toString())
    );
  }

  filterName(group: ChangeFilterGroup): string {
    return this.$t(`admin.clients.changes.filters.${group}`).toString();
  }

  filterKey(group: ChangeFilterGroup): string {
    return group;
  }

  /** A filter's key: the groups it leaves out. */
  viewKey(shownGroups: ChangeFilterGroup[]): string {
    return excludedChangeGroups(shownGroups).join(",");
  }

  async mounted() {
    await this.loadView(this.shownGroups);
    // Ready for the first change of the filter: the Verlauf with every group shown.
    this.loadView(CHANGE_FILTER_GROUPS);
  }

  onFilterChanged(shownGroups: ChangeFilterGroup[]) {
    this.shownGroups = shownGroups;
    const view = this.views[this.currentKey];
    if (view && !view.failed) {
      this.shownKey = this.currentKey;
    } else {
      // Not there yet, or it failed: fetch it, and keep the list on screen meanwhile.
      this.loadView(shownGroups);
    }
  }

  /**
   * Reads the Verlauf again, for an action on the detail that records a row without reloading the
   * detail (the Hilfsfunktionen card, #2610077). The list on screen stays until the new one is there;
   * the other filters' lists are dropped, so they are fetched afresh when chosen.
   */
  async reload() {
    for (const key of Object.keys(this.views)) {
      if (key !== this.shownKey) {
        Vue.delete(this.views, key);
      }
    }
    await this.loadView(this.shownGroups, true);
  }

  /**
   * Fetches a filter's first page, and shows it if that filter is still the current one. A list already
   * there is kept unless `force`.
   */
  async loadView(shownGroups: ChangeFilterGroup[], force = false) {
    const excluded = excludedChangeGroups(shownGroups);
    const key = excluded.join(",");
    if (this.loadingKeys.includes(key) || (!force && this.views[key]?.failed === false)) {
      return;
    }
    this.loadingKeys.push(key);
    try {
      const page = await getClientAdministrationChanges(this.clientId, null, excluded);
      Vue.set(this.views, key, { excluded, changes: page.changes, nextCursor: page.nextCursor, failed: false });
    } catch {
      // The card shows its own failure; the rest of the detail is unaffected.
      Vue.set(this.views, key, { excluded, changes: [], nextCursor: null, failed: true });
    } finally {
      this.loadingKeys = this.loadingKeys.filter((loading) => loading !== key);
    }
    if (key === this.currentKey) {
      this.shownKey = key;
    }
  }

  async loadMore() {
    const view = this.shownView;
    if (!view?.nextCursor) {
      return;
    }
    this.isLoadingMore = true;
    try {
      const page = await getClientAdministrationChanges(this.clientId, view.nextCursor, view.excluded);
      // Into that filter's own list, even if the filter changed meanwhile.
      view.changes = [...view.changes, ...page.changes];
      view.nextCursor = page.nextCursor;
    } catch {
      SnackbarModule.showError({ key: "admin.clients.changes.loadFailed" });
    } finally {
      this.isLoadingMore = false;
    }
  }
}
